import React from "react";
import { STATE_TONES, StateSquare } from "./WorkspaceBand.jsx";
import {
  billingStatusMeta,
  invoiceDisplayStatus,
  invoiceIssueBlockingReasons,
  invoiceIssueChecksPending,
  isInvoiceIssueBlocked,
} from "../../utils/billingPresentation.js";
import { buttonToneClasses, DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME, joinClasses } from "../resultsPrimitives/shared.js";
import { periodLabel } from "../../utils/billingPeriods.js";
import { Ban, Check, ChevronDown, ChevronUp, Eye, FileDown, FileSpreadsheet, FileText, MessageSquareText, Pencil, Send, Trash2 } from "lucide-react";
import ContextMenu, { ContextMenuItem, ContextMenuSeparator, useContextMenu } from "../ContextMenu.jsx";
import ConfirmationModal from "../ConfirmationModal.jsx";
import InvoiceCalculationPanel, { invoiceAccountItems, invoiceStatementTotals } from "./InvoiceCalculationPanel.jsx";
import TransportStatementSendButton from "./TransportStatementSendButton.jsx";
import TransportStatementLauncher from "./TransportStatementLauncher.jsx";
import { useBillingClientExplanation } from "./BillingClientExplanationLauncher.jsx";
import { fetchInvoice } from "../../api/billingApi.js";
import { isTransportStatement } from "../../utils/transportStatement.js";

const PILL_CLASS_NAME = DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME.replace("!", "");
// Prêt : vert Grain ; envoyé : coche verte sur fond blanc.
const STATUS_TONES = { draft: "done", issued: "issued", revalidation: "blocked", void: "void" };
const PRIMARY_BUTTON = joinClasses([
  "inline-flex h-7 shrink-0 items-center justify-center gap-1 border px-2 text-[11px] font-extrabold transition disabled:cursor-not-allowed disabled:opacity-45",
  PILL_CLASS_NAME,
  buttonToneClasses.optimizer,
]);
// Contour brique, sans fond pâle : ouvre la confirmation « Annuler le relevé ».
const CANCEL_BUTTON = joinClasses([
  "inline-flex h-7 shrink-0 items-center justify-center gap-1 border border-[#c5524a] bg-white px-2 text-[11px] font-extrabold text-[#c5524a] transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-45",
  PILL_CLASS_NAME,
]);

// Un relevé numéroté, envoyé ou non, s'annule ; ses prestations retournent dans « À facturer ».
function isCancellable(invoice, permissions) {
  return ["issued", "sent"].includes(invoiceDisplayStatus(invoice)) && Boolean(permissions.void);
}

function formatMoney(value, currency = "CAD") {
  return new Intl.NumberFormat("fr-CA", { style: "currency", currency: currency || "CAD" }).format(Number(value || 0));
}

function todayInputValue() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function invoiceAccountLabel(invoice) {
  return String(
    invoice?.billing_account_name
      || invoice?.billing_account_snapshot?.name
      || invoice?.billing_account?.name
      || invoice?.client_name
      || "",
  ).trim() || "Entreprise introuvable";
}

// Échec du PDF ou du courriel : le serveur ne l'écrit qu'après le dernier essai automatique.
const sendFailed = (invoice) => invoice.send_status === "failed" || invoice.document_status === "failed";
// Un essai automatique reste prévu (PDF ou courriel) : « Envoyer » n'est pas offert.
const sending = (invoice) => !sendFailed(invoice)
  && (["queued", "generating"].includes(invoice.document_status) || ["queued", "sending"].includes(invoice.send_status));
// Courriel peut-être parti (issue inconnue) : seul « Renvoyer quand même » le relance.
const RESEND_WARNING = "Le courriel est peut-être parti. Demandez à l'entreprise avant de renvoyer.";

// Relevé numéroté : « envoyé le 18 oct. » (date du courriel parti), ou l'envoi encore en cours (PDF, courriel) ou échoué.
function invoiceSendContext(invoice) {
  const status = invoiceDisplayStatus(invoice);
  if (status === "issued") return sendFailed(invoice) ? { label: "envoi échoué", danger: true } : { label: "envoi en cours", danger: false };
  if (!["sent", "paid"].includes(status) || !invoice.sent_at) return null;
  const sent = new Date(invoice.sent_at);
  return Number.isNaN(sent.getTime()) ? null
    : { label: `envoyé le ${new Intl.DateTimeFormat("fr-CA", { day: "numeric", month: "short" }).format(sent)}`, danger: false };
}

// « issue » : brouillon à numéroter et envoyer ; « send » : relevé numéroté dont le courriel n'est pas parti.
function invoiceCardAction(invoice) {
  const status = invoiceDisplayStatus(invoice);
  if (status === "draft" && invoiceIssueChecksPending(invoice)) return { intent: "issue", pending: true };
  if (["draft", "revalidation"].includes(status) && !isInvoiceIssueBlocked(invoice)) return { intent: "issue" };
  if (status === "issued") return sending(invoice) ? null : { intent: "send" };
  return null;
}

// Clic droit sur une carte : les actions de son état, l'action principale en tête, l'action destructive en bas.
function InvoiceMenuItems({ invoice, permissions, onOpen, onAction, onSend, onConfirm, onExplain }) {
  const status = invoiceDisplayStatus(invoice);
  const pdf = invoice.document_status === "generated";
  const open = (intent = "view") => () => onOpen?.(invoice.id, intent);
  const blocked = isInvoiceIssueBlocked(invoice);
  const pending = invoiceIssueChecksPending(invoice);
  const canVoid = isCancellable(invoice, permissions);
  if (status === "draft" || status === "revalidation") return <>
    <ContextMenuItem icon={blocked ? Pencil : FileText} primary onSelect={open()}>{blocked ? "Corriger le problème" : "Voir le détail"}</ContextMenuItem>
    <ContextMenuItem icon={MessageSquareText} onSelect={() => onExplain(invoice.id)}>Expliquer au client</ContextMenuItem>
    <ContextMenuItem icon={Eye} onSelect={() => onAction("preview-pdf", [invoice])}>Aperçu du PDF</ContextMenuItem>
    <ContextMenuItem icon={FileSpreadsheet} onSelect={() => onAction("excel", [invoice])}>Télécharger l’Excel</ContextMenuItem>
    {permissions.send && isTransportStatement(invoice) ? <ContextMenuItem icon={Send} disabled={blocked || pending}
      hint={pending ? "vérification…" : blocked ? "envoi bloqué" : ""} onSelect={() => onSend(invoice.id)}>Envoyer</ContextMenuItem> : null}
    {permissions.drafts ? <><ContextMenuSeparator /><ContextMenuItem icon={Trash2} danger onSelect={open("delete")}>Supprimer le brouillon…</ContextMenuItem></> : null}
  </>;
  return <>
    {status === "issued" && permissions.send && !sending(invoice) ? <ContextMenuItem icon={Send} primary
      onSelect={() => onConfirm({ action: "send", invoices: [invoice], resendAnyway: Boolean(invoice.send_ambiguous) })}>{invoice.send_ambiguous ? "Renvoyer quand même…" : "Envoyer"}</ContextMenuItem>
      : <ContextMenuItem icon={FileText} primary onSelect={open()}>Voir le détail</ContextMenuItem>}
    <ContextMenuItem icon={MessageSquareText} onSelect={() => onExplain(invoice.id)}>Expliquer au client</ContextMenuItem>
    <ContextMenuItem icon={FileDown} disabled={!pdf} hint={pdf ? "" : invoice.document_status === "failed" ? "PDF en échec" : "PDF en préparation"} onSelect={() => onAction("download", [invoice])}>Télécharger le PDF</ContextMenuItem>
    <ContextMenuItem icon={FileSpreadsheet} onSelect={() => onAction("excel", [invoice])}>Télécharger l’Excel</ContextMenuItem>
    {canVoid ? <><ContextMenuSeparator /><ContextMenuItem icon={Ban} danger onSelect={open("void")}>Annuler le relevé…</ContextMenuItem></> : null}
  </>;
}

// Plusieurs cartes (Ctrl+clic) : chaque action dit à combien de documents elle s'applique.
function SelectionMenuItems({ selection, permissions, onAction, onConfirm }) {
  const pdfs = selection.filter((invoice) => invoice.document_status === "generated");
  const sendable = permissions.send ? selection.filter((invoice) => invoiceDisplayStatus(invoice) === "issued" && !sending(invoice) && !invoice.send_ambiguous) : [];
  const part = (rows) => (rows.length && rows.length < selection.length ? `${rows.length} sur ${selection.length}` : "");
  return <>
    <ContextMenuItem icon={FileDown} disabled={!pdfs.length} hint={part(pdfs)} onSelect={() => onAction("download", pdfs)}>Télécharger les PDF</ContextMenuItem>
    <ContextMenuItem icon={FileSpreadsheet} onSelect={() => exportSelection(selection)}>Exporter la sélection (Excel)</ContextMenuItem>
    {permissions.send ? <><ContextMenuSeparator /><ContextMenuItem icon={Send} disabled={!sendable.length} hint={sendable.length ? part(sendable) : "aucun à envoyer"} onSelect={() => onConfirm({ action: "send", invoices: sendable })}>Envoyer…</ContextMenuItem></> : null}
  </>;
}

// Fichier CSV (séparateur « ; », BOM) que Excel ouvre directement.
function exportSelection(selection) {
  const cell = (value) => `"${String(value ?? "").replaceAll('"', '""')}"`;
  const amount = (value) => Number(value || 0).toFixed(2).replace(".", ",");
  const rows = [["Numéro", "Type", "Entreprise", "État", "Début", "Fin", "Commandes", "Envoyé le", "Total"],
    ...selection.map((invoice) => [invoice.invoice_number || "Brouillon", "Relevé",
      invoiceAccountLabel(invoice), billingStatusMeta(invoiceDisplayStatus(invoice)).label, invoice.period_start, invoice.period_end,
      invoice.order_count, String(invoice.sent_at || "").slice(0, 10)
        || (invoice.issued_at ? `Numéroté le ${String(invoice.issued_at).slice(0, 10)}, courriel non parti` : ""), amount(invoice.total)])];
  const url = URL.createObjectURL(new Blob(["\uFEFF" + rows.map((row) => row.map(cell).join(";")).join("\r\n")], { type: "text/csv;charset=utf-8" }));
  Object.assign(document.createElement("a"), { href: url, download: `releves-${todayInputValue()}.csv` }).click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Menu du clic droit : une carte, ou la sélection faite au Ctrl+clic.
export function InvoiceCardMenu({ menu, permissions, onOpen, onAction, onSend, onExplain, selection = null, onConfirm }) {
  return <ContextMenu menu={menu} label={selection ? "Actions de la sélection" : "Actions du relevé"}
    title={selection ? `${selection.length} documents sélectionnés` : menu.data ? invoiceAccountLabel(menu.data) : ""}
    subtitle={selection ? "Ctrl+clic ou Maj+clic pour en ajouter" : menu.data ? [...new Set([menu.data.invoice_number || "Brouillon", billingStatusMeta(invoiceDisplayStatus(menu.data)).label]), formatMoney(menu.data.total, menu.data.currency_code)].join(" · ") : ""}>
    {selection ? <SelectionMenuItems selection={selection} permissions={permissions} onAction={onAction} onConfirm={onConfirm} />
      : menu.data ? <InvoiceMenuItems invoice={menu.data} permissions={permissions} onOpen={onOpen} onAction={onAction} onSend={onSend} onConfirm={onConfirm} onExplain={onExplain} /> : null}
  </ContextMenu>;
}

function BulkConfirmation({ request, busy, onCancel, onConfirm }) {
  const count = request.invoices.length;
  const amounts = request.invoices.map((invoice) => `${invoice.invoice_number} · ${formatMoney(invoice.total, invoice.currency_code)}`).join("\n");
  return <ConfirmationModal eyebrow="Facturation" cornerRadius="grain" panelSize="narrow" contentDensity="compact" tone={request.resendAnyway ? "warning" : "accent"} isBusy={busy}
    title={request.resendAnyway ? "Renvoyer le relevé par courriel ?" : `Envoyer ${count} relevé${count > 1 ? "s" : ""} par courriel ?`}
    description={request.resendAnyway ? `${RESEND_WARNING}\n${amounts}` : amounts}
    confirmLabel={request.resendAnyway ? "Renvoyer quand même" : "Envoyer"} onCancel={onCancel} onConfirm={() => onConfirm(request.resendAnyway ? { resend_anyway: true } : {})} />;
}

// Comme la Consolidation : les relevés rangés par ce qu’il reste à faire à gauche, le relevé ouvert à droite.
const GROUPS = [
  ["fix", "À corriger", "blocked"], ["ready", "Prêts à envoyer", "done"],
  ["sent", "Envoyés", "done", 5], ["closed", "Terminés", "void", 0],
];
const STATUS_BAR = { fix: "#c5524a", ready: "var(--color-accent)", sent: "transparent", closed: "#94a3b8" };
// Brouillon → Envoyé → Annulé : le groupe d'un relevé, partagé avec les indicateurs du haut.
export function invoiceGroup(invoice) {
  const status = invoiceDisplayStatus(invoice);
  if (status === "revalidation" || (status === "draft" && isInvoiceIssueBlocked(invoice))) return "fix";
  if (status === "draft" || status === "issued") return "ready";
  return status === "void" ? "closed" : "sent";
}
const blockingMessages = (invoice) => invoiceIssueBlockingReasons(invoice).map((reason) => reason.message).filter(Boolean);
const SECONDARY_BUTTON = joinClasses([
  "inline-flex h-7 shrink-0 items-center justify-center gap-1 border border-slate-300 bg-white px-2 text-[11px] font-extrabold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-45",
  PILL_CLASS_NAME,
]);

export default function InvoiceCards({
  invoices = [],
  onOpen,
  onSelect,
  selectedInvoiceId,
  refreshKey,
  busy = false,
  permissions = {},
  onAction,
  detail = null,
  detailId = null,
  detailBlock = "",
  onCloseDetail,
  onSendEmailDirtyChange,
}) {
  const panelId = React.useId();
  const menu = useContextMenu();
  const [selectedIds, setSelectedIds] = React.useState(() => new Set());
  const [bulk, setBulk] = React.useState(null);
  // « Envoyer » du clic droit : { id, n } ; n remonte le bouton de la carte pour ouvrir son panneau.
  const [sendRequest, setSendRequest] = React.useState(null);
  const [expandedGroups, setExpandedGroups] = React.useState(() => new Set());
  // Relevé complet lu par « Comprendre le montant » : « Voir le relevé » en a besoin (lignes calculées).
  const [loaded, setLoaded] = React.useState(null);
  const selection = invoices.filter((invoice) => selectedIds.has(invoice.id));
  const menuSelection = menu.data && selectedIds.has(menu.data.id) && selection.length > 1 ? selection : null;
  const groups = GROUPS.map(([key, title, tone, limit]) => ({ key, title, tone, limit, rows: invoices.filter((invoice) => invoiceGroup(invoice) === key) }));
  const ordered = groups.flatMap((group) => group.rows);
  const opened = ordered.find((invoice) => invoice.id === selectedInvoiceId) || null;
  const next = opened ? ordered[ordered.indexOf(opened) + 1] : null;
  // Une carte disparue de la liste (filtre, actualisation) sort de la sélection.
  React.useEffect(() => {
    setSelectedIds((current) => {
      const kept = new Set([...current].filter((id) => invoices.some((invoice) => invoice.id === id)));
      return kept.size === current.size ? current : kept;
    });
  }, [invoices]);
  // Le premier relevé à traiter s’ouvre de lui-même : la colonne de droite n’est jamais vide.
  React.useEffect(() => { if (ordered.length && !opened) onSelect?.(ordered[0].id); }, [ordered[0]?.id, opened?.id]);
  const toggleSelected = (invoiceId) => setSelectedIds((current) => {
    const next = new Set(current);
    if (next.has(invoiceId)) next.delete(invoiceId); else next.add(invoiceId);
    return next;
  });
  const choose = (invoiceId) => { if (invoiceId !== selectedInvoiceId) { setSendRequest(null); onSelect?.(invoiceId); } };
  const requestSend = (invoiceId) => { choose(invoiceId); setSendRequest((current) => ({ id: invoiceId, n: (current?.n || 0) + 1 })); };
  // « Expliquer au client » du clic droit : la même explication que la carte, pour n'importe quel relevé de la liste.
  const { openExplanation, explanationDialog } = useBillingClientExplanation();
  const explain = async (invoiceId) => {
    let invoice = null;
    try { invoice = await fetchInvoice(invoiceId); } catch { /* la fenêtre affiche l'erreur */ }
    openExplanation(invoice && invoiceAccountItems(invoice), invoice && invoiceStatementTotals(invoice));
  };
  const item = (invoice) => {
    const active = invoice.id === selectedInvoiceId;
    const sendContext = invoiceSendContext(invoice);
    const reasons = blockingMessages(invoice);
    // Commandes de la Coop livrées dans la période sans relevé validé : le relevé peut partir quand même.
    const gap = (invoice.issue_notices || []).find((notice) => notice.code === "period_incomplete");
    const alert = active ? "text-[#ffb4ab]" : "text-[#c5524a]";
    const voided = invoiceDisplayStatus(invoice) === "void";
    const sent = invoiceGroup(invoice) === "sent";
    return <article key={invoice.id} data-selected={selectedIds.has(invoice.id) || undefined} onContextMenu={(event) => menu.openAt(event, invoice)}
      style={{ boxShadow: `inset 4px 0 0 ${STATUS_BAR[invoiceGroup(invoice)]}` }}
      className={joinClasses([
        "relative min-w-0 overflow-hidden border py-1 pl-3.5 pr-2.5",
        PILL_CLASS_NAME,
        active ? "border-[#1f2b35] bg-[#1f2b35] text-white" : "border-[#c3ccd6] bg-white text-slate-800 hover:border-slate-400",
        selectedIds.has(invoice.id) ? "ring-2 ring-accent" : "",
        menu.data?.id === invoice.id || (menuSelection && selectedIds.has(invoice.id)) ? "outline-2 outline-dashed outline-offset-2 outline-accent" : "",
      ])}>
      <div className="flex min-w-0 items-baseline justify-between gap-2">
        <h3 className="flex min-w-0 items-center gap-1 text-[12.5px] font-extrabold leading-tight">
          {sent ? <Check size={14} aria-hidden="true" className={`shrink-0 text-accent ${active ? "rounded-[4px] bg-white" : ""}`} /> : null}
          <button type="button" aria-expanded={active} aria-controls={panelId}
            className="min-w-0 cursor-pointer truncate text-left outline-none after:absolute after:inset-0 after:content-[''] focus-visible:after:outline-2 focus-visible:after:outline-accent"
            onClick={(event) => {
              // Ctrl+clic ou Maj+clic : ajoute la carte à la sélection (clic droit : actions groupées).
              if (event.ctrlKey || event.metaKey || event.shiftKey) { event.preventDefault(); toggleSelected(invoice.id); return; }
              setSelectedIds(new Set());
              choose(invoice.id);
            }}>{invoiceAccountLabel(invoice)}</button>
        </h3>
        <strong className={`shrink-0 text-[12.5px] font-extrabold tabular-nums leading-tight ${voided ? "line-through" : ""}`}>{formatMoney(invoice.total, invoice.currency_code)}</strong>
      </div>
      <p className={`truncate text-[11.5px] leading-tight ${active ? "text-slate-300" : "text-slate-500"}`}>
        {invoice.invoice_number || "Brouillon"}
        {invoice.period_start ? ` · ${periodLabel(invoice.period_start, invoice.period_end)}` : ""}
        {reasons.length ? <b className={alert}> · {reasons[0]}</b>
          : sendContext ? <span className={sendContext.danger ? `font-bold ${alert}` : ""}> · {sendContext.label}</span> : null}
        {gap ? <b className={active ? "text-[#f3c58a]" : "text-[#a86a24]"}> · ! {gap.count} sans relevé validé</b> : null}
        {voided ? <b className={alert}> · Annulé</b> : null}
      </p>
    </article>;
  };
  const status = opened ? invoiceDisplayStatus(opened) : "";
  const action = opened ? invoiceCardAction(opened) : null;
  const reasons = opened ? blockingMessages(opened) : [];
  const sent = ["sent", "paid"].includes(status) && !reasons.length;
  const ambiguous = status === "issued" && Boolean(opened?.send_ambiguous) && !sending(opened);
  const detailOpen = Boolean(opened) && detailId === opened.id;
  // Le panneau cesse d'être fixe quand le détail se déplie : si la liste l'avait fait défiler, son haut revient à l'écran.
  const panelRef = React.useRef(null);
  React.useLayoutEffect(() => {
    const panel = panelRef.current;
    if (detailOpen && panel && panel.getBoundingClientRect().top < panel.parentElement.getBoundingClientRect().top) panel.scrollIntoView({ block: "start" });
  }, [detailOpen, opened?.id]);
  const draft = ["draft", "revalidation"].includes(status);
  // Brouillon de relevé : « Envoyer » ouvre la confirmation du courriel sur la carte, sans passer par le détail.
  const sendHere = action?.intent === "issue" && isTransportStatement(opened);
  const sendAsked = sendRequest?.id === opened?.id;
  return (
    <div data-guide-area="" className="grid min-h-0 flex-1 items-start gap-2.5 overflow-auto lg:grid-cols-[minmax(260px,330px)_minmax(0,1fr)]">
      <nav aria-label="Relevés par état" className="grid min-w-0 gap-2.5">
        {groups.filter((group) => group.rows.length).map((group) => {
          const open = expandedGroups.has(group.key) || group.limit === undefined;
          const shown = open ? group.rows : group.rows.filter((invoice, index) => index < group.limit || invoice.id === selectedInvoiceId);
          return <section key={group.key} aria-label={group.title} className="grid min-w-0 gap-1">
            <h3 className="flex items-center gap-1.5 px-0.5 text-[10.5px] font-bold uppercase tracking-[0.06em] text-slate-700">{group.key === "sent" ? <Check size={12} aria-hidden="true" className="shrink-0 text-accent" /> : <StateSquare tone={group.tone} />}{group.title}<span className="ml-auto font-semibold">{group.rows.length}</span></h3>
            {shown.map(item)}
            {shown.length < group.rows.length ? <button type="button" onClick={() => setExpandedGroups((current) => new Set([...current, group.key]))}
              className="justify-self-start rounded-[4px] px-1 text-xs font-bold text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent">
              {group.limit === 0 ? `Afficher les ${group.rows.length}` : `+ ${group.rows.length - shown.length} autres`}</button> : null}
          </section>;
        })}
      </nav>
      {/* Détail déplié : le panneau défile avec la liste, sinon le bas du détail resterait hors d'atteinte. */}
      {opened ? <section ref={panelRef} aria-label="Relevé ouvert" className={`invoice-open-panel min-w-0 overflow-clip rounded-[4px] border border-[#1f2b35] bg-white ${detailOpen ? "" : "lg:sticky lg:top-0"}`}>
        <header className="flex flex-wrap items-center gap-x-2 gap-y-1 bg-[#1f2b35] px-2 py-1 text-white">
          <h2 className="min-w-0 text-[14px] font-extrabold leading-5">{invoiceAccountLabel(opened)}</h2>
          <span className={`inline-flex items-center gap-1 rounded-[4px] px-1.5 text-[11px] font-bold leading-4 ${sent ? "bg-white text-slate-700" : "text-white"}`} style={sent ? undefined : { background: STATE_TONES[reasons.length ? "blocked" : STATUS_TONES[status] || "void"] }}>
            {sent ? <Check size={12} aria-hidden="true" className="shrink-0 text-accent" /> : null}
            {reasons.length ? "Envoi bloqué" : billingStatusMeta(status).label}</span>
          <span className="text-[11px] text-slate-300">{[opened.invoice_number || "Brouillon",
            opened.period_start ? periodLabel(opened.period_start, opened.period_end) : "",
            opened.order_count ? `${opened.order_count} commande${opened.order_count > 1 ? "s" : ""}` : "", invoiceSendContext(opened)?.label].filter(Boolean).join(" · ")}</span>
          <strong className="ml-auto text-[14px] font-extrabold tabular-nums">{formatMoney(opened.total, opened.currency_code)}</strong>
        </header>
        {reasons.length ? <p role="alert" className="mx-2 mt-2 flex items-start gap-1.5 rounded-[4px] border border-slate-200 border-l-[3px] border-l-[#c5524a] px-2 py-1.5 text-xs text-slate-800">
          <StateSquare tone="blocked" /><span><b>Pourquoi l’envoi est bloqué :</b> {reasons.join(" · ")}</span></p>
          : ambiguous ? <p role="alert" className="mx-2 mt-2 flex items-start gap-1.5 rounded-[4px] border border-slate-200 border-l-[3px] border-l-[#c5524a] px-2 py-1.5 text-xs text-slate-800">
            <StateSquare tone="blocked" /><span>{RESEND_WARNING}</span></p> : null}
        <div className="p-1.5"><InvoiceCalculationPanel key={opened.id} invoiceId={opened.id} id={panelId} refreshKey={refreshKey} onLoad={setLoaded} /></div>
        <footer className="flex flex-wrap items-center justify-end gap-1.5 border-t border-slate-300 px-2 py-1 [&>button]:h-6">
          {isCancellable(opened, permissions) ? <button type="button" className={CANCEL_BUTTON} disabled={busy} onClick={() => onOpen?.(opened.id, "void")}>Annuler le relevé…</button> : null}
          {/* Excel du relevé, à recopier dans le logiciel de facturation. */}
          <button type="button" className={SECONDARY_BUTTON} disabled={busy} onClick={() => onAction?.("excel", [opened])}><FileSpreadsheet size={13} aria-hidden="true" />Excel</button>
          {/* Le document que reçoit l'entreprise : relevé (avec l'annexe Geotab) et PDF. */}
          {draft ? <><TransportStatementLauncher invoice={loaded?.id === opened.id ? loaded : null} className={SECONDARY_BUTTON} disabled={busy || Boolean(detailBlock)} title={detailBlock || undefined} />
            <button type="button" className={SECONDARY_BUTTON} disabled={busy || Boolean(detailBlock)} title={detailBlock || undefined} onClick={() => onAction?.("preview-pdf", [opened])}><FileText size={13} aria-hidden="true" />Voir le PDF</button></>
            : opened.document_status === "generated" ? <button type="button" className={SECONDARY_BUTTON} disabled={busy} onClick={() => onAction?.("download", [opened])}><FileDown size={13} aria-hidden="true" />PDF</button> : null}
          <button type="button" className={SECONDARY_BUTTON} aria-expanded={detailOpen} disabled={busy} onClick={() => (detailOpen ? onCloseDetail?.() : onOpen?.(opened.id))}>
            {detailOpen ? <>Masquer le détail<ChevronUp size={13} aria-hidden="true" /></> : <>{reasons.length ? "Corriger" : "Voir le détail"}<ChevronDown size={13} aria-hidden="true" /></>}</button>
          {sendHere && permissions.send ? <TransportStatementSendButton key={`${opened.id}:${sendAsked ? sendRequest.n : 0}`}
            defaultOpen={sendAsked} recipient={opened.billing_email || ""} canEditRecipient={Boolean(permissions.drafts)} version={opened.version} onDirtyChange={onSendEmailDirtyChange} className={PRIMARY_BUTTON}
            warning={(opened.issue_notices || []).find((notice) => notice.code === "period_incomplete")?.message || ""}
            disabled={busy || Boolean(detailBlock)} pending={Boolean(action.pending)} title={detailBlock || (action.pending ? "Vérification des sources Consolidation avant l’envoi…" : undefined)}
            onSend={(email) => onAction?.("send-statement", [opened], { recipient_email: email })} />
            // Relevé numéroté dont le courriel n'est pas parti : « Envoyer » demande seulement confirmation, comme « Envoyer… » d'une sélection.
            : action?.intent === "send" && permissions.send ? <button type="button" className={PRIMARY_BUTTON} disabled={busy}
            onClick={() => setBulk({ action: "send", invoices: [opened], resendAnyway: ambiguous })}>{ambiguous ? "Renvoyer quand même" : "Envoyer"}</button> : null}
          <button type="button" className={SECONDARY_BUTTON} disabled={!next} onClick={() => next && choose(next.id)}>Aller au suivant ↓</button>
        </footer>
        {detailOpen ? detail || <p role="status" className="border-t border-slate-300 px-2 py-2 text-sm text-slate-600">Chargement du détail…</p> : null}
      </section> : null}
      <InvoiceCardMenu menu={menu} permissions={permissions} onOpen={onOpen} onAction={onAction} onSend={requestSend} onExplain={explain} selection={menuSelection} onConfirm={setBulk} />
      {explanationDialog}
      {bulk ? <BulkConfirmation request={bulk} busy={busy} onCancel={() => setBulk(null)}
        onConfirm={async (payload) => { await onAction(bulk.action, bulk.invoices, payload); setBulk(null); setSelectedIds(new Set()); }} /> : null}
    </div>
  );
}
